import { Component, signal, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { injectQuery } from '@tanstack/angular-query-experimental';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

// Kanzi
import * as fromCustomer from '@kanzi-apes/kanzi-customer';
import { CustomerService } from '@kanzi-apes/kanzi-customer';
import * as fromAuth from '@kanzi-apes/kanzi-auth';
import { KanziUiModule } from '@kanzi-apes/kanzi-ui';
import {
  LocationCreateModel,
  LocationTypeFilterModel,
  OperatingHours,
  CountryFilterModel,
  StateCountryFilterModel,
  CityFilterModel,
  CustomerListModel,
} from '@kanzi-apes/kanzi-models';
import {
  WarehousesQueriesService,
  UbicacionesQueriesService,
} from '../../../queries';
import { OperationScheduleComponent } from '../../../components/operation-schedule/operation-schedule.component';
import { Store } from '@ngrx/store';

@Component({
    selector: 'kanzi-apes-warehouse-create',
    imports: [
        CommonModule,
        TranslateModule,
        FormsModule,
        ButtonModule,
        InputTextModule,
        InputTextareaModule,
        DropdownModule,
        MultiSelectModule,
        ToastModule,
        KanziUiModule,
        OperationScheduleComponent,
    ],
    providers: [MessageService],
    templateUrl: './warehouse-create.component.html',
    styleUrls: ['./warehouse-create.component.scss']
})
export class WarehouseCreateComponent {
  private readonly router = inject(Router);
  private readonly warehouseQueries = inject(WarehousesQueriesService);
  private readonly locationQueries = inject(UbicacionesQueriesService);
  private readonly messageService = inject(MessageService);
  private readonly store = inject(Store);
  private readonly customerService = inject(CustomerService);
  customerSelected = this.store.selectSignal(fromCustomer.getCustomerSelected);
  userLogged = this.store.selectSignal(fromAuth.getAuthUser);

  // Determines if customer multi-select should be shown
  // (when logged user's customer has a parent)
  showCustomerSelector = computed(
    () => this.userLogged()?.configs?.customer?.parent == null
  );

  // Query to load available customers (enabled only when multi-select is needed)
  customersQuery = injectQuery<CustomerListModel, Error>(() => ({
    queryKey: ['customers', { page_size: 1000 }],
    queryFn: () =>
      lastValueFrom(this.customerService.listCustomers({ page_size: 1000 })),
    enabled: this.showCustomerSelector(),
    staleTime: 1000 * 60 * 5,
  }));

  // Selected customer IDs for multi-select (plain property, not signal, for ngModel compatibility)
  selectedCustomerIds: number[] = [];

  // Form data
  warehouse: LocationCreateModel = {
    name: '',
    display_name: '',
    external_id: '',
    description: '',
    customer_ids: [],
    type_id: undefined,
    country_id: undefined,
    state_id: undefined,
    city_id: undefined,
    address: '',
    postal_code: '',
    phone: '',
    mobile: '',
    operating_hours: undefined,
  };

  // Validation errors
  postalCodeError = '';
  phoneError = '';
  mobileError = '';

  // Query for location types
  typesFilters = signal<LocationTypeFilterModel | null>({ name: 'WAREHOUSE' });
  typesQuery = this.locationQueries.useLocationTypes(this.typesFilters);

  // Geographic queries
  countriesFilters = signal<CountryFilterModel | null>({ page_size: 1000 });
  countriesQuery = this.locationQueries.useCountries(this.countriesFilters);

  statesFilters = signal<StateCountryFilterModel | null>(null);
  statesEnabled = signal(false);
  statesQuery = this.locationQueries.useStates(
    this.statesFilters,
    this.statesEnabled
  );

  citiesFilters = signal<CityFilterModel | null>(null);
  citiesEnabled = signal(false);
  citiesQuery = this.locationQueries.useCities(
    this.citiesFilters,
    this.citiesEnabled
  );

  // Mutation
  createMutation = this.warehouseQueries.useCreateWarehouse();

  /**
   * Handle schedule changes from OperationScheduleComponent
   */
  onSchedulesChange(operatingHours: OperatingHours): void {
    this.warehouse.operating_hours = operatingHours;
  }

  /**
   * Handle country selection change
   * Resets state and city when country changes
   */
  onCountryChange(): void {
    // Reset dependent fields
    this.warehouse.state_id = undefined;
    this.warehouse.city_id = undefined;

    // Update states filter and enable/disable states dropdown
    if (this.warehouse.country_id) {
      this.statesFilters.set({
        country_id: this.warehouse.country_id,
        page_size: 1000,
      });
      this.statesEnabled.set(true);
    } else {
      this.statesFilters.set(null);
      this.statesEnabled.set(false);
    }

    // Disable cities dropdown when country changes
    this.citiesEnabled.set(false);
  }

  /**
   * Handle state selection change
   * Resets city when state changes
   */
  onStateChange(): void {
    // Reset dependent field
    this.warehouse.city_id = undefined;

    // Update cities filter and enable/disable cities dropdown
    if (this.warehouse.state_id) {
      this.citiesFilters.set({
        state_id: this.warehouse.state_id,
        page_size: 1000,
      });
      this.citiesEnabled.set(true);
    } else {
      this.citiesFilters.set(null);
      this.citiesEnabled.set(false);
    }
  }

  /**
   * Validate postal code (3-10 alphanumeric characters)
   */
  validatePostalCode(): void {
    if (this.warehouse.postal_code) {
      const postalCode = this.warehouse.postal_code.trim();
      if (postalCode.length < 3 || postalCode.length > 10) {
        this.postalCodeError =
          'El código postal debe tener entre 3 y 10 caracteres';
      } else if (!/^[a-zA-Z0-9]+$/.test(postalCode)) {
        this.postalCodeError =
          'El código postal solo puede contener letras y números';
      } else {
        this.postalCodeError = '';
      }
    } else {
      this.postalCodeError = '';
    }
  }

  /**
   * Validate phone format: digits, +, -, spaces only. Min 7, max 20 characters
   */
  validatePhone(): void {
    if (this.warehouse.phone) {
      const phone = this.warehouse.phone.trim();
      const phoneRegex = /^[\d\s\-+]+$/;

      if (!phoneRegex.test(phone)) {
        this.phoneError =
          'El teléfono solo puede contener dígitos, +, -, y espacios';
      } else if (phone.length < 7 || phone.length > 20) {
        this.phoneError = 'El teléfono debe tener entre 7 y 20 caracteres';
      } else {
        this.phoneError = '';
      }
    } else {
      this.phoneError = '';
    }
  }

  /**
   * Validate mobile format: digits, +, -, spaces only. Min 7, max 20 characters
   */
  validateMobile(): void {
    if (this.warehouse.mobile) {
      const mobile = this.warehouse.mobile.trim();
      const phoneRegex = /^[\d\s\-+]+$/;

      if (!phoneRegex.test(mobile)) {
        this.mobileError =
          'El celular solo puede contener dígitos, +, -, y espacios';
      } else if (mobile.length < 7 || mobile.length > 20) {
        this.mobileError = 'El celular debe tener entre 7 y 20 caracteres';
      } else {
        this.mobileError = '';
      }
    } else {
      this.mobileError = '';
    }
  }

  /**
   * Check if phone format is valid
   */
  private isValidPhoneFormat(phone: string): boolean {
    const phoneRegex = /^[\d\s\-+]+$/;
    const trimmed = phone.trim();
    return (
      phoneRegex.test(trimmed) && trimmed.length >= 7 && trimmed.length <= 20
    );
  }

  /**
   * Check if form has validation errors
   */
  hasValidationErrors(): boolean {
    return !!(this.postalCodeError || this.phoneError || this.mobileError);
  }

  /**
   * Save new warehouse
   */
  onSave(): void {
    // Basic validation
    if (!this.warehouse.name?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'El nombre del warehouse es requerido',
      });
      return;
    }

    // Check for validation errors
    if (this.hasValidationErrors()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Por favor corrija los errores en el formulario',
      });
      return;
    }

    // Validate at least one customer is selected when multi-select is active
    if (this.showCustomerSelector() && this.selectedCustomerIds.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Debe seleccionar al menos un cliente',
      });
      return;
    }

    // Get WAREHOUSE type
    const warehouseType = this.typesQuery
      .data()
      ?.results?.find((t) => t.name === 'WAREHOUSE');

    if (!warehouseType?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo obtener el tipo WAREHOUSE',
      });
      return;
    }

    // Assign type and display_name if it doesn't exist
    const dataToSave: LocationCreateModel = {
      ...this.warehouse,
      display_name: this.warehouse.display_name || this.warehouse.name,
      type_id: warehouseType.id,
      customer_ids: this.showCustomerSelector()
        ? this.selectedCustomerIds
        : [this.customerSelected()?.id || 0],
    };

    this.createMutation.mutate(dataToSave, {
      onSuccess: (data) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Warehouse creado',
          detail: `El warehouse "${data.name}" fue creado exitosamente`,
        });

        // Navigate to detail
        if (data.id) {
          this.router.navigate(['locations', 'warehouses', 'detail', data.id]);
        } else {
          this.onCancel();
        }
      },
      onError: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error al crear',
          detail: error.message || 'Ocurrió un error al crear el warehouse',
        });
      },
    });
  }

  /**
   * Cancel and return to list
   */
  onCancel(): void {
    this.router.navigate(['locations', 'warehouses', 'list']);
  }
}
